import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useToast } from '../../components/toast';
import { internalSend, validationDetail } from '../../lib/api';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, Fields2 } from '@/components/ui/field';
import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';

/** Where a rate error is shown: under its field, or for the whole form (API refusal). */
type RateError = { target: 'date' | 'value' | 'form'; message: string } | null;

/**
 * « Saisir un taux » : a manual USD→EUR rate. Stays mounted: what was typed is
 * kept when the dialog is closed, and cleared after a successful save.
 */
export function RateDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [rateDate, setRateDate] = useState('');
  const [rateValue, setRateValue] = useState('');
  const [error, setError] = useState<RateError>(null);

  const close = (): void => {
    setError(null);
    onClose();
  };

  const addRate = useMutation({
    mutationFn: () =>
      internalSend('POST', '/internal/exchange-rates', { date: rateDate, usdToEur: rateValue }),
    onSuccess: () => {
      setRateDate('');
      setRateValue('');
      close();
      void queryClient.invalidateQueries({ queryKey: ['rates'] });
      notify('Taux enregistré');
    },
    onError: (failure) => {
      setError({
        target: 'form',
        message: validationDetail(failure) ?? 'Échec de l’enregistrement du taux.',
      });
    },
  });

  const submit = (): void => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(rateDate)) {
      setError({ target: 'date', message: 'Date requise (jj/mm/aaaa)' });
      return;
    }
    if (rateValue.trim().length === 0 || Number.isNaN(Number(rateValue))) {
      setError({ target: 'value', message: 'Taux invalide' });
      return;
    }
    addRate.mutate();
  };

  const errorFor = (target: 'date' | 'value'): string | undefined =>
    error !== null && error.target === target ? error.message : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent width={480}>
        <DialogHead title="Saisir un taux" description="Taux manuel USD vers EUR." />
        <form
          id="rate-form"
          className="contents"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <DialogBody>
            <Fields2>
              <Field label="Date" required error={errorFor('date')}>
                <DateInput
                  value={rateDate}
                  onChange={(value) => {
                    setRateDate(value);
                    setError(null);
                  }}
                />
              </Field>
              <Field label="Taux" required hint="1 $ en euros" error={errorFor('value')}>
                <Input
                  numeric
                  inputMode="decimal"
                  value={rateValue}
                  onChange={(event) => {
                    setRateValue(event.target.value);
                    setError(null);
                  }}
                  placeholder="0.92"
                />
              </Field>
            </Fields2>
            {error !== null && error.target === 'form' ? (
              <Callout variant="destructive">{error.message}</Callout>
            ) : null}
          </DialogBody>
        </form>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Annuler
          </Button>
          <Button type="submit" form="rate-form" size="lg" disabled={addRate.isPending}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
